<template>
    <v-app>
        <v-main v-if="organization_account != null">

            <m-make-payment-app-bar
                :value="organization_label"
                :banner_branding="banner_branding"
            />

            <v-container>
                <div style="margin-left: 15px; margin-right: 15px">

                    <div
                        v-if="show_thank_you"
                        style="text-align: center; margin-top: 30px; font-size: 32px"
                    >
                        <h1 class="text-h4 mb-5">
                            <strong>Thanks!</strong>
                        </h1>
                        We will be in touch soon.
                    </div>

                    <div v-else>
                        <h1
                            class="text-h6"
                            style="margin-top: 30px; margin-bottom: 20px"
                        >
                            Enter your information to receive text messages with information, promotions and offers from
                            {{ organization_account.label }}
                        </h1>

                        <rc-base-input
                            label="First Name"
                            type="text"
                            v-model="first_name"
                            autocomplete="given-name"
                            required
                        />

                        <rc-base-input
                            label="Last Name"
                            type="text"
                            v-model="last_name"
                            autocomplete="family-name"
                            required
                        />

                        <rc-base-input
                            label="Phone Number"
                            type="tel"
                            v-model="phone_number"
                            required
                            @validated="phone_input_valid = $event"
                        />

                        <div v-if="robust_age_gate">
                            <p>
                                You must be {{ default_age_gate_age }} or older to receive our text messages. Please
                                enter your birthday in MM/DD/YYYY format so we can verify your age
                            </p>

                            <rc-base-input
                                v-model="birthday"
                                label="Birthday"
                                type="birthday"
                                required
                                @validated="birthday_input_valid = $event"
                            />
                        </div>

                        <m-legal-checkbox-agreement
                            ref="agreement"
                            v-model="agreement_cb"
                            :organization_id="organization_id"
                            type="default-opt-in-form-agreement"
                            required
                        />

                        <rc-alert
                            v-model="show_alert"
                            color="primary"
                            type="error"
                            dismissible
                            style="margin-top: 15px"
                            :restore-focus-to="$refs.submitButton"
                        >
                            {{ alert_text }}
                        </rc-alert>

                        <div style="text-align: right; padding-top: 30px">
                            <v-btn
                                ref="submitButton"
                                color="secondary"
                                :disabled="!is_valid"
                                @click="onClickSubmit"
                            >
                                Submit
                            </v-btn>
                        </div>
                    </div>

                </div>
            </v-container>

        </v-main>


        <m-global-spinner />
        <m-global-snackbar />
        <m-global-alert />
        <m-global-confirm />
        <m-global-prompt />
    </v-app>
</template>

<script>
import RcBaseInput from '@/components/shared/inputs/rc-base-input.vue';
import RcAlert from '@/components/shared/alerts/rc-alert.vue';

export default {
    components: {
        RcAlert,
        RcBaseInput
    },

    data() {
        return {
            banner_branding: null,
            organization_account: null,
            agreement_cb: false,

            first_name: null,
            last_name: null,
            phone_number: null,
            phone_input_valid: false,
            birthday: null,
            birthday_input_valid: false,

            show_alert: false,
            alert_text: null,

            show_thank_you: false,
        }
    },

    mounted() {
        this.loadProfile();
        document.title = `Opt-In Form ${this.organization_label ? '| ' + this.organization_label : ''}`;
    },

    methods: {
        loadProfile() {
            this.$rc.rest({
                spinner: true,
                endpoint: 'PUT: /api/v1/opt-in-form/default/profile',

                params: {
                    organization_id: this.$route.params.organization_id,
                    banner_id: this.$route.params.banner_id,
                },

                on_success: (data) => {
                    this.banner_branding = data.banner_branding;
                    this.organization_account = data.organization_account;
                },

                on_error: 'Unable to get form information',
            });
        },

        onClickSubmit() {
            if (!this.is_valid) {
                // Should not be possible...
                this.alert_text = 'Please fill out all of the above fields.';
                this.show_alert = true;
                return;
            }

            this.$rc.rest({
                spinner: true,
                endpoint: 'PUT: /api/v1/opt-in-form/default/opt-in',

                params: {
                    organization_id: this.$route.params.organization_id,
                    first_name: this.first_name,
                    last_name: this.last_name,
                    phone_number: this.phone_number,
                    birthday: this.birthday,
                    agreement_specimen: this.$refs['agreement'].getSpecimen(),
                    submitted_from_url: '' + document.location,
                },

                on_success: (data) => {

                    if (data.valid == false) {
                        this.alert_text = data.message;
                        this.show_alert = true;
                        return;
                    }

                    this.show_thank_you = true;

                },

                on_error: 'Unable to opt in',
            });
        },

        isBlank(value) {
            if (value == null) return true;
            if (value.trim().length == 0) return true;
            return false;
        },
    },

    computed: {
        robust_age_gate() {
            if (this.organization_account == null)
                return false;

            return this.organization_account.industry == 'wine';
        },

        is_valid() {
            if (this.agreement_cb == false) return false;

            if (this.isBlank(this.first_name)) return false;
            if (this.isBlank(this.last_name)) return false;
            if (this.isBlank(this.phone_number) || !this.phone_input_valid) return false;
            if ((this.isBlank(this.birthday) || !this.birthday_input_valid) && this.robust_age_gate) return false;

            return true;
        },

        organization_label() {
            if (this.organization_account == null) return '';
            return this.organization_account.label;
        },

        organization_id() {
            if (this.organization_account == null) return '';
            return this.organization_account.organization_id;
        },

        default_age_gate_age() {
            return 21;
        },
    },
}
</script>

<style lang="scss" scoped>
/*
    When used as text/bg with white text, our primary color is not AA compliant on a white bg.
    This should be moved to global.css eventually ...
    But that will necessitate more work than just these styles for consistency sake.
    Only applying to public forms for now.
*/
div.v-application .primary--text {
    color: var(--v-primary-darken1) !important;
    caret-color: var(--v-primary-darken1) !important;
}

div.v-application .error--text {
    color: var(--v-error-darken1) !important;
    caret-color: var(--v-error-darken1) !important;
}

div.v-alert.primary {
    background-color: var(--v-primary-darken1) !important;
    border-color: var(--v-primary-darken1) !important;
}
</style>